import '../../i18n/autoTranslate';
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import SpotlightCard from '../../../components/ui/SpotlightCard';
import US from 'country-flag-icons/react/3x2/US';
import GB from 'country-flag-icons/react/3x2/GB';
import SA from 'country-flag-icons/react/3x2/SA';
import FR from 'country-flag-icons/react/3x2/FR';

import { FaPlay, FaStop } from 'react-icons/fa6';

const AIPersonalityCustomizer = ({
  embedded = false,
  personality,
  onPersonalityChange,
  className = '',
  elevenlabsVoice=''
}) => {
  const aiPersonas = [
    {
      id: 'professional',
      name: 'Frederic',
      accent: 'fr-FR',
      language: 'fr',
      avatar:
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      description: 'Professional, confident, and structured interviewer',
      traits: ['Professional', 'Confident', 'Structured'],
      gender: 'male',
      image: {
        value: 'fr-FR',
        label: 'French',
        flag: <FR title="France" className="h-6 w-10 rounded-md" />,
      },
      elevenlabs: {
        voice_id: 'oziFLKtaxVDHQAh7o45V',
        model_id: 'eleven_flash_v2_5',
      },
      audio: 'Frederic FR presentation',
    },

    {
      id: 'friendly',
      name: 'Mark',
      accent: 'en-US',
      language: 'en',
      avatar:
        'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&auto=format&fit=crop&q=60',
      description: 'Casual, relaxed, and friendly interviewer',
      traits: ['Friendly', 'Relaxed', 'Encouraging'],
      gender: 'male',
      image: {
        value: 'en-US',
        label: 'American English',
        flag: <US title="United States" className="h-6 w-10 rounded-md" />,
      },
      elevenlabs: {
        voice_id: '1SM7GgM6IMuvQlz2BwM3',
        model_id: 'eleven_flash_v2_5',
      },
      audio: 'Mark US presentation',
    },

    {
      id: 'challenging',
      name: 'Alexander Kensington',
      accent: 'en-GB',
      language: 'en',
      avatar:
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      description: 'Direct, analytical, high-pressure British interviewer',
      traits: ['Direct', 'Analytical', 'Challenging'],
      gender: 'male',
      image: {
        value: 'en-GB',
        label: 'British English',
        flag: <GB title="United Kingdom" className="h-6 w-10 rounded-md" />,
      },
      elevenlabs: {
        voice_id: 'mZ8K1MPRiT5wDQaasg3i',
        model_id: 'eleven_flash_v2_5',
      },
      audio: 'Alexander GB presentation',
    },

    {
      id: 'arabic',
      name: 'أحمد',
      accent: 'ar-SA',
      language: 'ar',
      avatar:
        'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
      description: 'Arabic interviewer, clear, respectful, and professional',
      traits: ['واضح', 'محترف', 'هادئ'],
      gender: 'male',
      image: {
        value: 'ar-SA',
        label: 'اللغة العربية',
        flag: <SA title="Saudi Arabia" className="h-6 w-10 rounded-md" />,
      },
      elevenlabs: {
        voice_id: '1SM7GgM6IMuvQlz2BwM3',
        model_id: 'eleven_flash_v2_5',
      },
      audio: 'أحمد  AR presentation',
    },
  ];

  const sliderSettings = [
    {
      key: 'tone',
      label: 'Interview Tone',
      icon: 'Volume2',
      min: 1,
      max: 5,
      labels: ['Very Casual', 'Casual', 'Balanced', 'Formal', 'Very Formal'],
    },
    {
      key: 'difficulty',
      label: 'Question Difficulty',
      icon: 'TrendingUp',
      min: 1,
      max: 5,
      labels: ['Beginner', 'Easy', 'Medium', 'Hard', 'Expert'],
    },
    {
      key: 'pace',
      label: 'Speaking Pace',
      icon: 'Clock',
      min: 1,
      max: 5,
      labels: ['Very Slow', 'Slow', 'Normal', 'Fast', 'Very Fast'],
    },
    {
      key: 'pressure',
      label: 'Pressure Level',
      icon: 'Zap',
      min: 1,
      max: 5,
      labels: ['Relaxed', 'Low', 'Medium', 'High', 'Intense'],
    },
  ];

  const accents = [
    { value: 'en-US', label: 'American English', flag: <US title="United States" className="h-6 w-10 rounded-md" /> },
    { value: 'en-GB', label: 'British English', flag: <GB title="United Kingdom" className="h-6 w-10 rounded-md" /> },
    { value: 'ar-SA', label: 'اللغة العربية', flag: <SA title="Saudi Arabia" className="h-6 w-10 rounded-md" /> },
    { value: 'fr-FR', label: 'French', flag: <FR title="France" className="h-6 w-10 rounded-md" /> },
  ];

  const handleSliderChange = (key, value) => {
    onPersonalityChange({
      ...personality,
      [key]: value,
    });
  };

  const handlePersonaSelect = (personaId) => {
    const selectedPersona = aiPersonas.find((p) => p.id === personaId);
    onPersonalityChange({
      ...personality,
      selectedPersona: selectedPersona,
      accent: selectedPersona?.accent,
      elevenlabsVoice: selectedPersona?.elevenlabs?.voice_id || elevenlabsVoice,
    });
  };

  const audioRef = useRef(null);
  const [playingPersonaId, setPlayingPersonaId] = useState(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, []);

  const togglePersonaAudio = (personaId, personaName) => {
    try {
      const currentAudio = audioRef.current;
      const isSamePersona = playingPersonaId === personaId;

      if (currentAudio && isSamePersona && !currentAudio.paused) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        audioRef.current = null;
        setPlayingPersonaId(null);
        return;
      }

      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }

      const audio = new Audio(`/audio/${personaName}.mp3`);
      audioRef.current = audio;
      setPlayingPersonaId(personaId);

      audio.onended = () => {
        if (audioRef.current === audio) audioRef.current = null;
        setPlayingPersonaId(null);
      };
      audio.onerror = () => {
        if (audioRef.current === audio) audioRef.current = null;
        setPlayingPersonaId(null);
      };

      audio.play().catch(() => {
        if (audioRef.current === audio) audioRef.current = null;
        setPlayingPersonaId(null);
      });
    } catch (err) {
      console.error('Audio error:', err);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      setPlayingPersonaId(null);
    }
  };

  const panel =
    'relative overflow-hidden rounded-3xl border border-white/10 bg-[#0b0f16]/60 backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_18px_60px_-40px_rgba(56,189,248,0.18)]';

  const headIcon =
    'flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-gradient-to-br from-sky-500/15 via-violet-500/10 to-emerald-500/10 shadow-[0_0_22px_rgba(56,189,248,0.20)]';

  return (
    <div className={`p-0 w-full transition-all duration-200 ease-smooth space-y-8 ${className}`}>
      {!embedded && (
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl">
              <img src="/ai_sjqlnq.png" alt="ai_personality" className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-white">AI Interviewer Personality</h2>
          </div>
          <p className="text-white/60">Customize your AI interviewer's style and approach.</p>
        </div>
      )}

      {/* Persona Selection */}
      <div className="space-y-4">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center gap-3">
            <div className={headIcon}>
              <Icon name="Bot" size={16} className="text-white" />
            </div>
            <h3 className="text-lg font-semibold text-white">Choose Your Interviewer</h3>
          </div>

          <p className="text-sm text-white/60 leading-relaxed max-w-2xl">
            Each interviewer speaks and conducts the interview in their own language and accent.
            The entire interview — including questions, guidance, and feedback — will be delivered
            in the same language as the interviewer you select.
            <br />
            <span className="font-medium text-white">Choose an interviewer that matches your practice language.</span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-fr">
          {aiPersonas?.map((persona) => {
            const isSelected = personality?.selectedPersona?.id === persona?.id;
            const isPlaying = playingPersonaId === persona?.id;

            return (
              <motion.button
                key={persona?.id}
                type="button"
                onClick={() => handlePersonaSelect(persona?.id)}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.99 }}
                transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                className="group h-full text-left"
              >
                <SpotlightCard
                  className="custom-spotlight-card h-full rounded-3xl overflow-hidden"
                  spotlightColor={isSelected ? 'rgba(56,189,248,0.26)' : 'rgba(255,255,255,0.06)'}
                >
                  <div
                    className={`relative h-full rounded-3xl border transition-all duration-300 overflow-hidden backdrop-blur-xl
                      ${
                        isSelected
                          ? 'border-white/18 bg-gradient-to-br from-sky-500/18 via-violet-500/10 to-emerald-500/10 shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_18px_60px_-40px_rgba(56,189,248,0.55)]'
                          : 'border-white/10 bg-[#0b0f16]/60 hover:border-white/16 hover:bg-white/[0.04]'
                      }
                    `}
                  >
                    <div className="absolute inset-0 opacity-60 pointer-events-none [background:radial-gradient(circle_at_25%_25%,rgba(255,255,255,0.10),transparent_60%)]" />

                    <div className="relative p-5 sm:p-6 h-full flex flex-col">
                      <div className="flex items-start gap-4">
                        <div
                          className={`relative w-14 h-14 rounded-2xl overflow-hidden border flex-shrink-0 transition-all duration-200
                            ${
                              isSelected
                                ? 'border-white/18 shadow-[0_0_18px_rgba(56,189,248,0.30)]'
                                : 'border-white/10 group-hover:border-white/16'
                            }
                          `}
                        >
                          <img
                            src={persona?.avatar}
                            alt={persona?.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '/assets/images/no_image_jp3rlq.png';
                            }}
                          />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h4
                                className={`font-semibold transition-colors truncate ${
                                  isSelected ? 'text-white' : 'text-white'
                                }`}
                              >
                                {persona?.name}
                              </h4>
                              <div className="mt-1 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-xs text-white/75">
                                {persona?.image?.flag}
                                <span className="truncate">{persona?.image?.label}</span>
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePersonaAudio(persona?.id, persona?.audio);
                              }}
                              className="relative flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-gradient-to-br from-sky-500/35 via-violet-500/30 to-emerald-500/25 text-white hover:shadow-[0_0_22px_rgba(56,189,248,0.50)] transition-all duration-300 overflow-hidden shrink-0"
                              aria-label={`${isPlaying ? 'Stop' : 'Play'} ${persona?.name} preview`}
                            >
                              <span className="absolute -bottom-1.5 left-[20%] w-1/2 h-4 rounded-full bg-white/70 blur-[10px] pointer-events-none" />
                              {isPlaying ? <FaStop /> : <FaPlay />}
                            </button>
                          </div>

                          <p className="mt-3 text-sm text-white/70 leading-relaxed">
                            {persona?.description}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {persona?.traits?.map((trait) => (
                          <span
                            key={trait}
                            className={`px-2.5 py-1 text-xs rounded-full border ${
                              isSelected
                                ? 'bg-white/[0.06] text-white border-white/12'
                                : 'bg-white/[0.03] text-white/70 border-white/10'
                            }`}
                          >
                            {trait}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </SpotlightCard>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Sliders */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className={headIcon}>
            <Icon name="Sliders" size={16} className="text-white" />
          </div>
          <h3 className="text-lg font-semibold text-white">Fine-tune Personality</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 auto-rows-fr">
          {sliderSettings?.map((setting) => (
            <div key={setting?.key} className={`${panel} p-5 space-y-3`}>
              <div className="pointer-events-none absolute inset-0 opacity-60 [background:radial-gradient(circle_at_25%_25%,rgba(255,255,255,0.08),transparent_60%)]" />

              <div className="relative flex items-center gap-2">
                <div className="flex items-center justify-center w-9 h-9 rounded-2xl border border-white/10 bg-white/[0.04]">
                  <Icon name={setting?.icon} size={16} className="text-white" />
                </div>
                <label className="text-sm font-medium text-white">{setting?.label}</label>
              </div>

              <div className="relative space-y-2">
                <div className="relative">
                  <input
                    type="range"
                    min={setting?.min}
                    max={setting?.max}
                    value={personality?.[setting?.key] || 3}
                    onChange={(e) =>
                      handleSliderChange(setting?.key, parseInt(e?.target?.value, 10))
                    }
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #38BDF8 0%, #38BDF8 ${
                        ((personality?.[setting?.key] || 3) - 1) * 25
                      }%, rgba(255,255,255,0.12) ${
                        ((personality?.[setting?.key] || 3) - 1) * 25
                      }%, rgba(255,255,255,0.12) 100%)`,
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-white/45">
                  <span>{setting?.labels?.[0]}</span>
                  <span className="font-medium text-white px-2 py-1 bg-white/[0.04] rounded-2xl border border-white/10">
                    {setting?.labels?.[(personality?.[setting?.key] || 3) - 1]}
                  </span>
                  <span>{setting?.labels?.[4]}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="pt-6 border-t border-white/10">
        <div className={`${panel} p-5`}>
          <div className="pointer-events-none absolute inset-0 opacity-60 [background:radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.14),transparent_60%)]" />
          <div className="relative flex items-start gap-3">
            <div className={headIcon}>
              <Icon name="Settings" size={16} className="text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-white mb-2">Current Configuration</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-white/60">
                <div className="flex items-start gap-2">
                  <Icon name="User" size={12} className="text-white mt-0.5 flex-shrink-0" />
                  <span>Interviewer: {personality?.selectedPersona?.name || 'None selected'}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Icon name="Globe" size={12} className="text-white mt-0.5 flex-shrink-0" />
                  <span>
                    Accent: {accents.find((a) => a.value === personality?.accent)?.label || 'Not selected'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPersonalityCustomizer;
